import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { isNumber, sumBy } from 'lodash';
import * as moment from 'moment/moment';
import { BehaviorSubject } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import {
  ActiveWorkflowStepI,
  WorkflowStepI,
} from '@dashboards/components/avg-turn-chart-card/avg-turn-chart.interface';
import { KpiTile } from '@dashboards/models/kpi-tile';
import {
  KpiGroup,
  KpiTableExpandedRow,
  KpiTableRow,
  PortfolioKpiType,
  RestPortfolioKpi,
  kpiGroupWindowConfig,
  kpiWorkflowStep,
} from '@dashboards/models/portfolio-kpi';
import { KpiWindowTypeListConst } from '@shared/constants/kpi-window-type-list.const';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { KpiWindowType } from '@shared/enums/kpi-window-type';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { RestThresholdsConfiguration } from '@shared/interfaces/thresholds-configuration.interface';
import { selectWorkflowStepName } from '@shared/observables/select-workflow-step';

@Injectable({
  providedIn: 'root',
})
export class KpiService {
  windowType$ = new BehaviorSubject<KpiWindowType>(null);
  workflowStep$ = new BehaviorSubject<WorkflowStepI>(null);
  activeWorkflowStep$ = new BehaviorSubject<ActiveWorkflowStepI>(null);

  constructor(private restApiService: RestapiService, private store: Store) {}

  private filterKpiByType(kpis: RestPortfolioKpi[], kpiType: PortfolioKpiType): RestPortfolioKpi[] {
    return kpis.filter(k => k.kpiType === kpiType);
  }

  avg(kpis: RestPortfolioKpi[], kpiType: PortfolioKpiType, precision = 0, currentMonth = null): number | undefined {
    let filtered = this.filterKpiByType(kpis, kpiType);
    if (currentMonth) {
      filtered = filtered.filter(el => moment(el.calculationDate).format('MMM YYYY') === currentMonth);
    }
    const avg = sumBy(filtered, k => k.value * k.unitsCount) / sumBy(filtered, k => k.unitsCount);
    if (isNaN(avg)) {
      return undefined;
    } else {
      return !precision ? Math.round(avg) : parseFloat(avg.toFixed(precision));
    }
  }

  sum(kpis: RestPortfolioKpi[], kpiType: PortfolioKpiType, precision = 0): number {
    const filtered = this.filterKpiByType(kpis, kpiType);
    return parseFloat(sumBy(filtered, k => k.value).toFixed(precision));
  }

  percent(kpis: RestPortfolioKpi[], kpiType: PortfolioKpiType): number | undefined {
    const filtered = this.filterKpiByType(kpis, kpiType);
    const value = sumBy(filtered, k => k.value) / sumBy(filtered, k => k.unitsCount);
    if (isNaN(value)) {
      return undefined;
    }
    return Math.round(value * 100);
  }

  fetch(portfolioId: number, dateOffset: KpiWindowType, kpiTypes: PortfolioKpiType[]) {
    return this.restApiService.getData<RestPortfolioKpi[]>(
      `portfolio/${portfolioId}/kpis?dateOffset=${dateOffset}&${kpiTypes.map(k => `kpiIds=${k}`).join('&')}`
    );
  }

  trend(portfolioId: number, kpiTypes: PortfolioKpiType[], trendDays: number[]) {
    return this.restApiService.getData<RestPortfolioKpi[]>(
      `portfolio/${portfolioId}/kpisTrend?kpi=[${kpiTypes.map(k => k).join(',')}]&trend_line=[${trendDays.join(',')}]`
    );
  }

  calculateInventoryKpis(
    kpis: RestPortfolioKpi[],
    todayKpis: RestPortfolioKpi[],
    pastKpis: RestPortfolioKpi[],
    trendDaysValue: number,
    windowType: KpiWindowType
  ): KpiTile[] {
    return [
      {
        icon: EIcon.HOME_HEART2,
        title: 'Occupancy',
        kpiGroup: KpiGroup.Occupancy,
        tooltip: (value: number) => {
          const total = sumBy(
            kpis.filter(x => x.kpiType === PortfolioKpiType.OccupancyPercent),
            x => x.unitsCount
          );
          const occupiedCnt = total * (value / 100);
          return `${Math.round(occupiedCnt)} occupied units / ${total} total units`;
        },
        subTitle: '',
        trendDirection: true,
        value: this.avg(todayKpis, PortfolioKpiType.OccupancyPercent),
        measureType: '%',
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.OccupancyPercent),
            trendDays: KpiWindowType.Months12,
          },
        ],
      },
      {
        icon: EIcon.ALARM_CLOCK,
        title: 'Expirations',
        kpiGroup: KpiGroup.Expirations,
        tooltip: () => {
          const label = KpiWindowTypeListConst.find(e => e.value == windowType)?.label;
          return `Noticed leases${label ? ` within ${label}` : ''} without a renewal/move-out decision`;
        },
        subTitle: '',
        values: [
          { value: this.sum(todayKpis, PortfolioKpiType.Expirations30), trendDays: KpiWindowType.OpenTurns },
          { value: this.sum(todayKpis, PortfolioKpiType.Expirations30), trendDays: KpiWindowType.Days30 },
          { value: this.sum(todayKpis, PortfolioKpiType.Expirations60), trendDays: KpiWindowType.Days60 },
          { value: this.sum(todayKpis, PortfolioKpiType.Expirations90), trendDays: KpiWindowType.Days90 },
        ],
        trendValues: [
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Expirations30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Expirations30),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Expirations60),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Expirations90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendDirection: false,
      },
      {
        icon: EIcon.SIGN_OUT,
        kpiGroup: KpiGroup.MoveOuts,
        title: 'Move-outs',
        tooltip: () => {
          const label = KpiWindowTypeListConst.find(e => e.value == windowType)?.label;
          return `Tenants who are on notice to leave${label ? ` within ${label}` : ''}`;
        },
        subTitle: '',
        values: [
          { value: this.sum(todayKpis, PortfolioKpiType.MoveOuts30), trendDays: KpiWindowType.OpenTurns },
          { value: this.sum(todayKpis, PortfolioKpiType.MoveOuts30), trendDays: KpiWindowType.Days30 },
          { value: this.sum(todayKpis, PortfolioKpiType.MoveOuts60), trendDays: KpiWindowType.Days60 },
          { value: this.sum(todayKpis, PortfolioKpiType.MoveOuts90), trendDays: KpiWindowType.Days90 },
        ],
        trendValues: [
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.MoveOuts30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.MoveOuts30),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.MoveOuts60),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.MoveOuts90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendDirection: false,
      },
      {
        icon: EIcon.CLIPBOARD_LIST,
        iconColor: EColorPalette.cGreen10,
        kpiGroup: KpiGroup.Applications,
        title: 'Applications',
        tooltip: value => {
          const newCnt = this.getNewValue(kpis, trendDaysValue);
          const canceledCnt = this.getCanceledValue(kpis, trendDaysValue);
          const deniedCnt = this.getDeniedValue(kpis, trendDaysValue);
          const pendingCnt = this.getPendingValue(kpis, trendDaysValue);
          const approvedCnt = this.getApprovedValue(kpis, trendDaysValue);
          const label = KpiWindowTypeListConst.find(e => e.value == windowType)?.label;

          return `Applications${
            label ? ` within the past ${label}` : ''
          } using this formula: new (${newCnt}) - canceled (${canceledCnt}) - denied (${deniedCnt}) + pending (${pendingCnt}) + approved (${approvedCnt}) = ${value}`;
        },
        subTitle: '',
        values: [
          {
            value: this.sum(todayKpis, PortfolioKpiType.ApplicationsTotal30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.sum(todayKpis, PortfolioKpiType.ApplicationsTotal30),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.sum(todayKpis, PortfolioKpiType.ApplicationsTotal60),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.sum(todayKpis, PortfolioKpiType.ApplicationsTotal90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendValues: [
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.ApplicationsTotal30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.ApplicationsTotal30),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.ApplicationsTotal60),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.ApplicationsTotal90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendDirection: true,
      },
      {
        icon: EIcon.REPEAT2,
        kpiGroup: KpiGroup.PreLeased,
        title: 'Pre-leased',
        tooltip: () => {
          const label = KpiWindowTypeListConst.find(e => e.value == windowType)?.label;
          return `Signed leases set to move-in${label ? ` within next ${label}` : ''}`;
        },
        subTitle: '',
        values: [
          { value: this.sum(todayKpis, PortfolioKpiType.Preleased30), trendDays: KpiWindowType.OpenTurns },
          { value: this.sum(todayKpis, PortfolioKpiType.Preleased30), trendDays: KpiWindowType.Days30 },
          { value: this.sum(todayKpis, PortfolioKpiType.Preleased60), trendDays: KpiWindowType.Days60 },
          { value: this.sum(todayKpis, PortfolioKpiType.Preleased90), trendDays: KpiWindowType.Days90 },
        ],
        trendValues: [
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Preleased30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Preleased30),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Preleased60),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.sum(pastKpis, PortfolioKpiType.Preleased90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendDirection: true,
      },
      {
        icon: EIcon.ESTIMATED_OCCUPANCY,
        kpiGroup: KpiGroup.EstOccupancy,
        title: 'Est. Occupancy',
        tooltip: () => {
          const label = KpiWindowTypeListConst.find(e => e.value == windowType)?.label;
          return `Occupancy - Move Outs - (est. % of expirations) + Preleased + (est. % of applications) all${
            label ? ` within the next ${label}` : ''
          }`;
        },
        subTitle: '',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.EstOccupancyPercent30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.EstOccupancyPercent30),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.EstOccupancyPercent60),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.EstOccupancyPercent90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.EstOccupancyPercent30),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.EstOccupancyPercent30),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.EstOccupancyPercent60),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.EstOccupancyPercent90),
            trendDays: KpiWindowType.Days90,
          },
        ],
        measureType: '%',
        trendDirection: true,
      },
    ];
  }

  calculateTimeKpis(
    todayKpis: RestPortfolioKpi[],
    pastKpis: RestPortfolioKpi[],
    thresholdsConfiguration: RestThresholdsConfiguration
  ): KpiTile[] {
    return [
      {
        icon: EIcon.SIGN_OUT,
        kpiGroup: KpiGroup.MoveOut,
        kpiGroupId: WorkflowStepEnumType.MoveOut,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.MoveOut),
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOutNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOut30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOut60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOut90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOut6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveOut1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOutNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOut30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOut60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOut90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOut6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveOut1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        measureType: 'day',
        workflowStepEnumType: WorkflowStepEnumType.MoveOut,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.MAKE_READY,
        kpiGroup: KpiGroup.MakeReady,
        kpiGroupId: WorkflowStepEnumType.MakeReady,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.MakeReady),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReadyNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReady30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReady60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReady90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReady6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMakeReady1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReadyNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReady30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReady60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReady90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReady6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMakeReady1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.MakeReady,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.RENO,
        kpiGroup: KpiGroup.Reno,
        kpiGroupId: WorkflowStepEnumType.Reno,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.Reno),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgRenoNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgReno30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgReno60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgReno90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgReno6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgReno1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgRenoNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgReno30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgReno60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgReno90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgReno6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgReno1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.Reno,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.CLIPBOARD_LIST,
        iconColor: EColorPalette.cGreen10,
        kpiGroup: KpiGroup.Punch,
        kpiGroupId: WorkflowStepEnumType.Punch,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.Punch),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunchNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunch30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunch60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunch90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunch6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgPunch1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunchNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunch30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunch60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunch90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunch6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgPunch1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.Punch,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.THUMB_UP,
        iconColor: EColorPalette.cGreen10,
        kpiGroup: KpiGroup.Approval,
        kpiGroupId: WorkflowStepEnumType.Approval,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.Approval),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApprovalNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApproval30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApproval60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApproval90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApproval6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgApproval1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApprovalNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApproval30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApproval60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApproval90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApproval6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgApproval1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.Approval,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.KEY,
        kpiGroup: KpiGroup.LeaseUp,
        kpiGroupId: WorkflowStepEnumType.LeaseUp,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.LeaseUp),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUpNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUp30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUp60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUp90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUp6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgLeaseUp1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUpNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUp30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUp60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUp90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUp6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgLeaseUp1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.LeaseUp,
        thresholdsConfiguration,
      },
      {
        icon: EIcon.MOVE_IN,
        kpiGroup: KpiGroup.MoveIn,
        kpiGroupId: WorkflowStepEnumType.MoveIn,
        title: '',
        subTitleAsync: selectWorkflowStepName(this.store, WorkflowStepEnumType.MoveIn),
        measureType: 'day',
        values: [
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveInNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveIn30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveIn60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveIn90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveIn6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(todayKpis, PortfolioKpiType.AvgMoveIn1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveInNow, 2),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveIn30, 2),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveIn60, 2),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveIn90, 2),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveIn6m, 2),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.AvgMoveIn1y, 2),
            trendDays: KpiWindowType.Months12,
          },
        ],
        workflowStepEnumType: WorkflowStepEnumType.MoveIn,
        thresholdsConfiguration,
      },
    ];
  }

  calculateCommonKpis(
    kpis: RestPortfolioKpi[],
    pastKpis: RestPortfolioKpi[],
    thresholdsConfiguration: RestThresholdsConfiguration,
    windowType: KpiWindowType
  ): KpiTile[] {
    return [
      {
        icon: EIcon.TIME_SCHEDULE2,
        title: 'Avg Time to Turn',
        subTitle: '',
        values: [
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurnNow, 1),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurn30, 1),
            trendDays: KpiWindowType.Days30,
          },
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurn60, 1),
            trendDays: KpiWindowType.Days60,
          },
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurn90, 1),
            trendDays: KpiWindowType.Days90,
          },
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurn6m, 1),
            trendDays: KpiWindowType.Months6,
          },
          {
            value: this.avg(kpis, PortfolioKpiType.TimeToTurn1y, 1),
            trendDays: KpiWindowType.Months12,
          },
        ],
        trendValues: [
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurnNow, 1),
            trendDays: KpiWindowType.OpenTurns,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurn30, 1),
            trendDays: KpiWindowType.Days30,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurn60, 1),
            trendDays: KpiWindowType.Days60,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurn90, 1),
            trendDays: KpiWindowType.Days90,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurn6m, 1),
            trendDays: KpiWindowType.Months6,
          },
          {
            getValue: () => this.avg(pastKpis, PortfolioKpiType.TimeToTurn1y, 1),
            trendDays: KpiWindowType.Months12,
          },
        ],
        measureType: 'day',
        thresholdsConfiguration,
      },
      {
        icon: EIcon.REPEAT_ONE,
        title: 'Unleased',
        subTitle: '',
        value: this.percent(
          kpis,
          windowType === KpiWindowType.OpenTurns ? PortfolioKpiType.UnleasedPercent : PortfolioKpiType.UnleasedUnits
        ),
        measureType: '%',
      },
    ];
  }

  getNewValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.NewApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.NewApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.NewApplications30);
      }
    }
  }

  getCanceledValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.CancelledApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.CancelledApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.CancelledApplications30);
      }
    }
  }

  getPendingValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.PendingApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.PendingApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.PendingApplications30);
      }
    }
  }

  getDeniedValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.DeniedApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.DeniedApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.DeniedApplications30);
      }
    }
  }

  getApprovedValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.ApprovedApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.ApprovedApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.ApprovedApplications30);
      }
    }
  }

  getConvertingValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.ConvertingApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.ConvertingApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.ConvertingApplications30);
      }
    }
  }

  getConvertedValue(kpis: RestPortfolioKpi[], trendDaysValue: number) {
    switch (trendDaysValue) {
      case 60: {
        return this.sum(kpis, PortfolioKpiType.ConvertedApplications60);
      }
      case 90: {
        return this.sum(kpis, PortfolioKpiType.ConvertedApplications90);
      }
      default: {
        return this.sum(kpis, PortfolioKpiType.ConvertedApplications30);
      }
    }
  }

  getKpiConfig(
    kpiGroup: KpiGroup,
    windowType: KpiWindowType
  ): { type: PortfolioKpiType; method: 'avg' | 'sum' } | undefined {
    const groupConfig = kpiGroupWindowConfig[kpiGroup];
    if (!groupConfig) return undefined;
    return groupConfig[windowType];
  }

  prepareKpiTableData(
    kpiGroup: KpiGroup,
    kpiWindowType: KpiWindowType,
    propertyIds: number[],
    currentData: RestPortfolioKpi[],
    pastData: RestPortfolioKpi[],
    wideTrend: RestPortfolioKpi[]
  ): KpiTableRow[] {
    const config = this.getKpiConfig(kpiGroup, kpiWindowType);
    if (!config) return [];

    const rows: KpiTableRow[] = [];
    propertyIds.forEach(propertyId => {
      const currentKpis = currentData.filter(k => k.propertyId === propertyId && k.kpiType === config.type);
      const pastKpis = pastData.filter(k => k.propertyId === propertyId && k.kpiType === config.type);
      const wideTrendKpis = wideTrend.filter(k => k.propertyId === propertyId && k.kpiType === config.type);

      let currentValue: number | undefined;
      let pastValue: number | undefined;
      let wideTrendValue: number | undefined;

      switch (config.method) {
        case 'avg':
          currentValue = this.avg(currentKpis, config.type, 2);
          pastValue = this.avg(pastKpis, config.type, 2);
          wideTrendValue = this.avg(wideTrendKpis, config.type, 2);
          break;
        case 'sum':
          currentValue = this.sum(currentKpis, config.type, 2);
          pastValue = this.sum(pastKpis, config.type, 2);
          wideTrendValue = this.sum(wideTrendKpis, config.type, 2);
          break;
      }

      const totalUnits = sumBy(currentKpis, 'unitsCount');

      let trendPercent = 0;
      if (currentValue !== undefined && pastValue !== undefined && pastValue !== 0) {
        trendPercent = ((currentValue - pastValue) / pastValue) * 100;
      }

      let wideTrendPercent = 0;
      if (currentValue !== undefined && wideTrendValue !== undefined && wideTrendValue !== 0) {
        wideTrendPercent = ((currentValue - wideTrendValue) / wideTrendValue) * 100;
      }

      rows.push({
        propertyId,
        totalUnits,
        kpiValue: currentValue,
        trendPercent,
        wideTrendPercent,
      });
    });

    return rows;
  }

  getAccumulatedKpiData(
    kpiGroup: KpiGroup,
    kpiWindowType: KpiWindowType,
    name: string,
    id: number,
    data: KpiTableRow[],
    pastKpis: RestPortfolioKpi[],
    wideTrendKpis: RestPortfolioKpi[],
    thresholdsConfiguration: RestThresholdsConfiguration
  ): KpiTableExpandedRow {
    const totalUnits = sumBy(data, 'totalUnits');
    const kpiValue =
      data
        .filter(d => isNumber(d.kpiValue))
        .map(d => d.kpiValue * d.totalUnits)
        .reduce((a, b) => a + b, 0) / totalUnits;

    const workflowStepEnumType = kpiWorkflowStep[kpiGroup];
    const result = {
      id,
      name,
      totalUnits,
      kpiValue,
      trendPercent: undefined,
      wideTrendPercent: undefined,
      data,
      thresholdsConfiguration,
      workflowStepEnumType,
    };
    const config = this.getKpiConfig(kpiGroup, kpiWindowType);
    if (!config) return result;

    let trendValue: number | undefined;
    let wideTrendValue: number | undefined;
    switch (config.method) {
      case 'avg':
        trendValue = this.avg(pastKpis, config.type, 2);
        wideTrendValue = this.avg(wideTrendKpis, config.type, 2);
        break;
      case 'sum':
        trendValue = this.sum(pastKpis, config.type, 2);
        wideTrendValue = this.sum(wideTrendKpis, config.type, 2);
        break;
    }
    const trendPercent = trendValue ? ((kpiValue - trendValue) / trendValue) * 100 : undefined;
    const wideTrendPercent = wideTrendValue ? ((kpiValue - wideTrendValue) / wideTrendValue) * 100 : undefined;
    return {
      ...result,
      trendPercent,
      wideTrendPercent,
    };
  }
}
